import React, {
  Children,
  type ReactNode,
  type RefObject,
  useMemo,
  useRef,
  useState,
} from 'react';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';

import 'swiper/swiper.css';
// @ts-ignore - Swiper 12 CSS module paths
import 'swiper/css/effect-fade';

interface SliderProps {
  wrapperClass?: string;
  children: ReactNode;
  className?: string;
  pagination?: boolean;
  navigation?: boolean;
  sliderWrapperClass?: string;
  spaceBetween?: number;
  loop?: boolean;
  autoplay?: boolean;
  fade?: boolean; // Use fade effect for smooth crossfade transitions
  customPagination?: (props: {
    currentIndex: number;
    total: number;
    goToSlide: (index: number) => void;
  }) => ReactNode;
  customNavigation?: (props: {
    prevRef: RefObject<HTMLButtonElement | null>;
    nextRef: RefObject<HTMLButtonElement | null>;
    total: number;
  }) => ReactNode;
  onSlideChange?: (index: number) => void;
  slideClass?: string;
}

const Slider = (props: SliderProps) => {
  const {
    children,
    className,
    wrapperClass,
    sliderWrapperClass,
    slideClass,
    pagination,
    navigation,
    spaceBetween,
    loop,
    autoplay,
    fade,
    customPagination,
    customNavigation,
    onSlideChange,
  } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const slides = useMemo(() => Children.toArray(children), [children]);
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);

  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);

  const handleSlideChange = (swiper: SwiperCore) => {
    setCurrentIndex(swiper.realIndex);

    if (onSlideChange) {
      onSlideChange(swiper.realIndex);
    }
  };

  const goToSlide = (index: number) => {
    swiperInstance?.slideTo(index);
  };

  const modules = useMemo(() => {
    const modules = [];
    if (pagination) {
      modules.push(Pagination);
    }
    if (navigation) {
      modules.push(Navigation);
    }
    if (autoplay) {
      modules.push(Autoplay);
    }
    if (fade) {
      modules.push(EffectFade);
    }
    return modules;
  }, [pagination, navigation, autoplay, fade]);

  const shouldShowDefaultPagination = pagination && !customPagination;
  const navigationConfig = useMemo(() => {
    if (customNavigation) {
      return {
        prevEl: prevRef.current,
        nextEl: nextRef.current,
      };
    }
    return navigation;
  }, [customNavigation, navigation]);

  return (
    <div className={`relative w-full h-full ${wrapperClass}`}>
      <Swiper
        wrapperClass={`h-full ${sliderWrapperClass}`}
        className={`${className} h-full`}
        modules={modules}
        pagination={shouldShowDefaultPagination ? undefined : pagination}
        navigation={navigationConfig}
        onSwiper={setSwiperInstance}
        onSlideChange={handleSlideChange}
        loop={loop}
        spaceBetween={spaceBetween}
        autoplay={autoplay ? { delay: 5000, disableOnInteraction: false } : false}
        effect={fade ? 'fade' : undefined}
        fadeEffect={fade ? { crossFade: true } : undefined}
        speed={fade ? 800 : 300}
        allowTouchMove={!fade}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index} className={slideClass}>{slide}</SwiperSlide>
        ))}
      </Swiper>

      {pagination && customPagination ? (
        customPagination({ currentIndex, total: slides.length, goToSlide })
      ) : (
        <></>
      )}
      {navigation && customNavigation ? (
        customNavigation({ prevRef, nextRef, total: slides.length })
      ) : (
        <></>
      )}
    </div>
  );
};

export default Slider;
